import { useLanguage } from "@/contexts/LanguageContext";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { translations } from "@/translations";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { motion } from "framer-motion";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { PageCTASection } from "@/components/PageCTASection";
import { usePageMeta } from "@/hooks/usePageMeta";

const Trilingual = () => {
  const { language } = useLanguage();
  const t = translations[language];

  usePageMeta({
    title: language === 'es'
      ? "Colegio Trilingüe en Vigo | Inglés, castellano y gallego – Gándara School"
      : `${t.trilingual.title} – Gándara School`,
    description: t.trilingual.metaDescription,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner />
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="max-w-4xl mx-auto mb-16 text-center">
            <h1 className="text-5xl font-light text-primary mb-6 animate-fade-in">
              {t.trilingual.title}
            </h1>

            <div className="flex justify-center gap-12 mb-12">
              <div className="flex flex-col items-center group">
                <div className="relative w-32 h-20 mb-4 overflow-hidden rounded-lg shadow-lg transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl">
                  <OptimizedPicture
                    src="/lovable-uploads/f5d287d8-245c-4c10-a740-4300dae57acf.png"
                    alt="Bandera de Galicia"
                    width={128}
                    height={80}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>
                <span className="text-base font-medium text-muted-foreground group-hover:text-[#009B48] transition-colors duration-300">
                  Galego
                </span>
              </div>
              
              <div className="flex flex-col items-center group">
                <div className="relative w-32 h-20 mb-4 overflow-hidden rounded-lg shadow-lg transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl">
                  <OptimizedPicture
                    src="/lovable-uploads/2182f666-461a-4733-b51a-333bcc850d03.png"
                    alt="Bandera de España"
                    width={128}
                    height={80}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>
                <span className="text-base font-medium text-muted-foreground group-hover:text-[#C60B1E] transition-colors duration-300">
                  Español
                </span>
              </div>
              
              <div className="flex flex-col items-center group">
                <div className="relative w-32 h-20 mb-4 overflow-hidden rounded-lg shadow-lg transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl">
                  <OptimizedPicture
                    src="/lovable-uploads/f6a31331-ccf3-4575-b318-91e93ba171a0.png"
                    alt="Union Jack"
                    width={128}
                    height={80}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>
                <span className="text-base font-medium text-muted-foreground group-hover:text-[#CF142B] transition-colors duration-300">
                  English
                </span>
              </div>
            </div>
            
            <div className="flex justify-center mb-12">
              <Button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                size="lg"
                className="gap-2 shadow-lg hover:shadow-xl transition-all flex items-center"
              >
                <MessageCircle className="h-5 w-5" />
                <span>{t.trilingual.bookVisit}</span>
              </Button>
            </div>
          </div>
          
          <div className="prose max-w-4xl mx-auto text-muted-foreground space-y-8">
            <div className="text-lg space-y-6">
              <p>En Gándara International School, creemos que <strong>el dominio de múltiples lenguas es esencial</strong> para el desarrollo integral de nuestros estudiantes.</p>
              <p>Por ello, hemos diseñado un <strong>entorno educativo trilingüe con el inglés como lengua principal</strong>, en el que las tres lenguas (inglés, castellano y gallego) conviven de manera natural, sin abandonar el pensamiento abstracto y lógico, enriqueciendo la experiencia de aprendizaje y fomentando una comprensión profunda de diversas culturas.</p>
            </div>

            <h2 className="text-3xl font-light text-foreground mt-16 mb-8">
              <strong>El inglés como lengua principal, con acompañantes especializados</strong>
            </h2>
            <p className="text-lg">Reconocemos la importancia del inglés como lengua global. Para garantizar una adquisición efectiva y natural de este idioma, <strong>cada una de nuestras aulas cuenta con la presencia constante de un acompañante que se comunica exclusivamente en inglés</strong>.</p>
            <p className="text-lg">Este contacto diario <strong>permite a los estudiantes familiarizarse con el idioma de manera espontánea</strong>, desarrollando habilidades comunicativas sólidas desde temprana edad.</p>
            <p className="text-lg">Y lo decimos con honestidad, como recoge el <a href={`/${language}/${{ es: 'metodologia-montessori', en: 'montessori-education', pt: 'metodologia-montessori', fr: 'methode-montessori' }[language]}`} className="text-primary hover:text-primary-hover">Método Gándara</a>: <strong>no somos un colegio británico ni una academia de inglés</strong>. El inglés es vehículo de aprendizaje, no un fin publicitario; no prometemos bilingüismo instantáneo, prometemos una <strong>progresión medida</strong> que se acompaña y se evalúa curso a curso.</p>

            <div className="my-12">
              <OptimizedPicture 
                src="/lovable-uploads/d15eba1f-878e-4986-8d95-0c50908fd851.png"
                alt="Grupo de niños con profesor en el colegio"
                width={600}
                height={400}
                loading="lazy"
                decoding="async"
                className="w-full max-w-[600px] h-auto mx-auto rounded-xl shadow-lg"
              />
            </div>

            <h2 className="text-3xl font-light text-foreground mt-16 mb-8">
              <strong>Fomento del castellano y el gallego en el currículo</strong>
            </h2>
            <p className="text-lg">Como escuela ubicada en Galicia, <strong>valoramos profundamente nuestra herencia cultural y lingüística</strong>.</p>
            <p className="text-lg">Por ello, integramos tanto el castellano como el gallego en nuestro plan de estudios, asegurando que los alumnos se sientan <strong>cómodos y competentes en ambas lenguas oficiales de la región</strong>. Esta práctica no solo refuerza su identidad cultural, sino que también amplía sus horizontes comunicativos.</p>

            <h2 className="text-3xl font-light text-foreground mt-16 mb-8">
              <strong>Beneficios cognitivos y sociales del trilingüismo</strong>
            </h2>
            <p className="text-lg">
              <a href="https://digibug.ugr.es/bitstream/handle/10481/70923/TFM21%20-BENAISA%20HAMED%2C%20DINA.pdf?sequence=1&isAllowed=y" target="_blank" rel="noreferrer noopener" className="text-primary hover:text-primary-hover">Diversos estudios</a> han demostrado que el aprendizaje de múltiples idiomas desde una edad temprana:
            </p>
            <ul className="list-disc pl-8 space-y-2 my-6">
              <li className="text-lg">Potencia las <strong>habilidades cognitivas</strong></li>
              <li className="text-lg">Mejora la <strong>capacidad de atención</strong></li>
              <li className="text-lg">Fortalece la <strong>resolución de problemas</strong></li>
            </ul>
            <p className="text-lg">Además, nuestros estudiantes desarrollan una <strong>mayor empatía y comprensión</strong> hacia otras culturas, preparándolos para desenvolverse con éxito en entornos multiculturales.</p>

            <div className="my-12">
              <OptimizedPicture 
                src="/lovable-uploads/bosque-cabana-lectura.png"
                alt="Colegio Bilingüe con Inglés en Vigo"
                width={600}
                height={800}
                loading="lazy"
                decoding="async"
                className="w-full max-w-[600px] h-auto mx-auto rounded-xl shadow-lg"
              />
            </div>

            <h2 className="text-3xl font-light text-foreground mt-16 mb-8">
              <strong>Metodología activa y vivencial</strong>
            </h2>
            <p className="text-lg">En <a href={`/${language}`} className="text-primary hover:text-primary-hover">Gándara International School</a>, adoptamos una pedagogía basada en el aprendizaje activo y vivencial, con una sólida base de <a href={`/${language}/${{ es: 'metodologia-montessori', en: 'montessori-education', pt: 'metodologia-montessori', fr: 'methode-montessori' }[language]}`} className="text-primary hover:text-primary-hover">colegio Montessori</a>.</p>
            <p className="text-lg">Nuestros <strong>espacios están diseñados para promover la exploración y el descubrimiento</strong>, permitiendo que los niños sean protagonistas de su propio proceso educativo. La integración de tres idiomas en este contexto facilita una adquisición lingüística natural y efectiva.</p>

            <h2 className="text-3xl font-light text-foreground mt-16 mb-8">
              <strong>Un entorno enriquecedor y multicultural</strong>
            </h2>
            <p className="text-lg">Nuestra comunidad educativa está compuesta por <strong>familias y profesionales de diversas procedencias</strong>, lo que enriquece el ambiente escolar y ofrece a los estudiantes una perspectiva global. Las actividades culturales y los proyectos colaborativos en los tres idiomas fomentan la interacción y el respeto mutuo, creando un espacio donde cada lengua y cultura es valorada.</p>
            <p className="text-lg">En resumen, el <strong>enfoque trilingüe</strong> de Gándara International School no solo busca la competencia lingüística en inglés, castellano y gallego, sino que también <strong>aspira a formar individuos conscientes, respetuosos y preparados</strong> para los desafíos de un mundo interconectado.</p>

            <PageCTASection variant="accent" />
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default Trilingual;
